/* =============================================================
   useDashboard.js — Real dashboard data from Supabase
   -------------------------------------------------------------
   Fetches all students (with documents) and partner universities
   in parallel queries, then derives everything the Dashboard needs:

     stats    — numeric counters for the four stat cards
     pipeline — students with incomplete documents, sorted by
                urgency score descending then alphabetically

   PIPELINE LOGIC (identical for incoming and outgoing):
     Only students with at least one non-complete document appear.
     Students with all docs approved/not_required are excluded.

     Score = max across all required documents of:
       base score:
         rejected     → 60
         missing      → 40  (required doc not uploaded yet)
         pending      → 25  (uploaded, awaiting IO review)
         complete     → 0
       + deadline bonus (only on incomplete docs):
         ≤ 7 days     → +50  → total ≥ 75 = Urgent
         8–14 days    → +20  → total ≥ 75 = Urgent if combined
         > 14 days    → +0

     Levels:
       score ≥ 75   → Urgent
       score 1–74   → Action Needed
       score 0      → excluded (all docs complete)

     Tie-breaking: equal score → alphabetical by student name.

   TAB-FREEZE RESILIENCE:
     Every Supabase query carries an AbortSignal. A hard 8-second
     timeout fires controller.abort() if any fetch hangs (e.g. Chrome
     froze the tab mid-request and the fetch promise never resolves).
     This guarantees setLoading(false) always runs, so skeleton loaders
     never get stuck.

     A visibilitychange listener re-runs load() the moment the user
     switches back to this tab, ensuring fresh data even if the
     previous fetch was aborted.

   ✓ DONE (backend integration):
   - Replaces incomingStudents/outgoingStudents/partners from mockData
   - Two parallel Supabase queries (students + partners)
   - Documents status derived from embedded documents relation
   - Pipeline sorted by urgency score descending
   ============================================================= */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase }               from '../lib/supabase';
import { useAuth }                from '../context/AuthContext';
import { resolveProcessTemplate } from '../lib/studentCategory';

/* ─── THRESHOLDS ─────────────────────────────────────────────── */
const DEADLINE_URGENT_DAYS  = 7;
const DEADLINE_WARNING_DAYS = 14;
const FETCH_TIMEOUT_MS      = 8000;

function daysUntilDeadline(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(dateStr + 'T00:00:00') - today) / 86_400_000);
}


/* ─── docBaseScore ───────────────────────────────────────────── */
function docBaseScore(status) {
  if (status === 'rejected') return 60;
  if (status === 'missing')  return 40;
  if (status === 'pending')  return 25;
  return 0; // approved, uploaded, not_required
}


/* ─── deadlineBonus ──────────────────────────────────────────── */
// Applied on top of docBaseScore — only for incomplete documents.
function deadlineBonus(days) {
  if (days === null || days < 0) return 0;
  if (days <= DEADLINE_URGENT_DAYS)  return 50;
  if (days <= DEADLINE_WARNING_DAYS) return 20;
  return 0;
}


/* ─── urgencyLevel ───────────────────────────────────────────── */
function urgencyLevel(score) {
  if (score >= 75) return 'Urgent';
  if (score > 0)   return 'Action Needed';
  return 'Monitoring';
}


/* ─── actionLabel ────────────────────────────────────────────── */
function actionLabel(worstStatus) {
  if (worstStatus === 'rejected') return 'Document Rejected';
  if (worstStatus === 'missing')  return 'Document Missing';
  if (worstStatus === 'pending')  return 'Document Pending';
  return 'All documents complete';
}


/* ─── studentPipelineScore ───────────────────────────────────── */
// Computes the max urgency score for a student across all their
// required documents. Returns { score, worstStatus }.
//   uploadedDocs    — [{ review_status, doc_name }] from documents table
//   requiredNames   — [string] doc names required for this student's type
//   deadlineByDoc   — { [doc_name]: deadline_date_string }
function studentPipelineScore(uploadedDocs, requiredNames, deadlineByDoc) {
  let maxScore    = 0;
  let worstStatus = null;

  // 1. Uploaded docs — check rejected and pending
  for (const doc of uploadedDocs) {
    const base = docBaseScore(doc.review_status);
    if (base === 0) continue; // approved/not_required → no contribution
    const days  = daysUntilDeadline(deadlineByDoc[doc.doc_name] ?? null);
    const score = base + deadlineBonus(days);
    if (score > maxScore) { maxScore = score; worstStatus = doc.review_status; }
  }

  // 2. Missing docs — required but not uploaded at all
  const uploadedNames = new Set(uploadedDocs.map(d => d.doc_name).filter(Boolean));
  for (const name of requiredNames) {
    if (uploadedNames.has(name)) continue; // already counted above
    const base  = docBaseScore('missing'); // 40
    const days  = daysUntilDeadline(deadlineByDoc[name] ?? null);
    const score = base + deadlineBonus(days);
    if (score > maxScore) { maxScore = score; worstStatus = 'missing'; }
  }

  return { score: maxScore, worstStatus };
}


/* ─── useDashboard HOOK ──────────────────────────────────────── */
export function useDashboard() {
  const { user } = useAuth();

  const [stats,    setStats]    = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Ref to the current AbortController so we can cancel in-flight fetches
  // when: (a) a new load starts, (b) the component unmounts.
  const abortRef     = useRef(null);
  // Retry mechanism: when a load is aborted (tab-freeze timeout) we retry with
  // exponential backoff. We stop retrying after MAX_AUTO_RETRIES so we don't
  // spin forever, but we expose an `error` state + manual `refetch` so the user
  // can recover from a stuck dashboard without closing the browser.
  const retryTimerRef  = useRef(null);
  const retryCountRef  = useRef(0);
  const MAX_AUTO_RETRIES = 5;
  // Backoff: 1s, 2s, 4s, 8s, 16s — capped at 16s.
  const backoffMs = (n) => Math.min(16_000, 1000 * 2 ** n);

  const load = useCallback(async () => {
    if (!user?.universityId) {
      // No auth-resolved user yet → don't get stuck on the skeleton. Clear the
      // loading flag so the dashboard renders empty state instead of skeleton
      // forever. When auth resolves and universityId becomes available, the
      // [user?.universityId] effect dependency will re-trigger load().
      setLoading(false);
      return;
    }

    // Cancel any pending retry timer and previous in-flight load.
    clearTimeout(retryTimerRef.current);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // Hard timeout: if any fetch hangs (e.g. Chrome froze the tab), abort
    // after 8 s so the finally block always runs and clears the spinner.
    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setError(null);

    try {
        // Five parallel queries for speed.
        // NOTE: documents are fetched with an explicit separate query instead of an
        // embedded join to avoid RLS issues that can cause embedded joins to
        // silently return empty results.
        const [studentsRes, partnersRes, templatesRes, docsRes, processRes] = await Promise.all([
          supabase
            .from('students')
            .select('id, name, type, status, home_university, destination_university, semester_id, process_template_id, degree_level, exchange_type, eu_status')
            .eq('archived', false)
            .abortSignal(signal),
          supabase
            .from('partner_universities')
            .select('country')
            .abortSignal(signal),
          // Fetch all process templates with their steps and documents.
          // Steps are now linked to templates (not directly to university/type).
          supabase
            .from('university_process_templates')
            .select('id, type, semester_id, match_degree, match_categories, is_default, process_steps(label, step_order, process_step_documents(item_type, deadline_date, document_types(name, type)))')
            .eq('university_id', user.universityId)
            .order('created_at')
            .abortSignal(signal),
          supabase
            .from('documents')
            .select('student_id, review_status, doc_name')
            .eq('university_id', user.universityId)
            .abortSignal(signal),
          // Current step index per student (set by the IO process stepper)
          supabase
            .from('student_process')
            .select('student_id, current_step')
            .abortSignal(signal),
        ]);

        // If the signal fired while we were awaiting (timeout), bail out and
        // schedule a retry with exponential backoff.
        if (signal.aborted) {
          if (retryCountRef.current < MAX_AUTO_RETRIES) {
            const delay = backoffMs(retryCountRef.current);
            retryCountRef.current++;
            retryTimerRef.current = setTimeout(() => load(), delay);
          } else {
            setError('Connection issue — could not load the dashboard. Tap retry.');
          }
          return;
        }

        // docsMap: { [student_id]: [{ review_status, doc_name }] }
        const docsMap = {};
        for (const d of (docsRes.data ?? [])) {
          if (!docsMap[d.student_id]) docsMap[d.student_id] = [];
          docsMap[d.student_id].push({ review_status: d.review_status, doc_name: d.doc_name });
        }

        // processMap: { [student_id]: current_step_index }
        const processMap = {};
        for (const row of (processRes.data ?? [])) {
          processMap[row.student_id] = row.current_step;
        }

        // templateDataMap: { [templateId]: { labels, requiredDocNames, deadlineByDoc } }
        // Pre-computed once per template — then each student looks up their own template.
        const templates = templatesRes.data ?? [];
        const templateDataMap = {};
        for (const t of templates) {
          const sortedSteps = (t.process_steps ?? []).slice().sort((a, b) => a.step_order - b.step_order);
          const labels          = [];
          const requiredDocNames = [];
          const deadlineByDoc   = {};
          for (const step of sortedSteps) {
            if (step.label) labels.push(step.label);
            for (const psd of (step.process_step_documents ?? [])) {
              if (psd.item_type === 'task') continue;
              const docName = psd.document_types?.name;
              const docKind = psd.document_types?.type;
              if (!docName || docKind === 'task') continue;
              if (psd.deadline_date) deadlineByDoc[docName] = psd.deadline_date;
              if (!requiredDocNames.includes(docName)) requiredDocNames.push(docName);
            }
          }
          templateDataMap[t.id] = { labels, requiredDocNames, deadlineByDoc };
        }

        const students = studentsRes.data ?? [];
        const partners = partnersRes.data ?? [];

        /* ── Pipeline rows (same logic for incoming and outgoing) ── */
        const allRows = students.map(s => {
          // Resolve which template applies to this student
          const typeTemplates = templates.filter(t => t.type === s.type);
          const resolvedTemplate = resolveProcessTemplate(typeTemplates, s);
          const tData = resolvedTemplate ? (templateDataMap[resolvedTemplate.id] ?? {}) : {};

          const uploadedDocs   = docsMap[s.id] ?? [];
          const requiredNames  = tData.requiredDocNames ?? [];
          const deadlineByDoc  = tData.deadlineByDoc    ?? {};
          const { score, worstStatus } = studentPipelineScore(
            uploadedDocs, requiredNames, deadlineByDoc
          );
          const isUrgent = urgencyLevel(score) === 'Urgent';

          // Derive status from the student's current process step label.
          // Falls back to students.status only if no steps are configured.
          const stepLabels  = tData.labels ?? [];
          const stepIdx     = processMap[s.id] ?? 0;
          const stepLabel   = stepLabels.length > 0
            ? (stepLabels[stepIdx] ?? s.status)
            : s.status;
          const isCompleted = stepLabels.length > 0
            && stepIdx >= stepLabels.length - 1;

          return {
            student:     s.name,
            id:          s.id,
            destination: s.type === 'incoming'
              ? (s.home_university        ?? '—')
              : (s.destination_university ?? '—'),
            type:        s.type === 'incoming' ? 'Incoming' : 'Outgoing',
            status:      stepLabel,
            isCompleted,
            score,
            isUrgent,
            action:      actionLabel(worstStatus),
            route:       `/${s.type}/${s.id}`,
            level:       urgencyLevel(score),
          };
        });

        /* ── Stat card values ─────────────────────────────── */
        const incoming    = students.filter(s => s.type === 'incoming');
        const outgoing    = students.filter(s => s.type === 'outgoing');
        const partnerCount  = partners.length;
        const countryCount  = new Set(partners.map(p => p.country)).size;
        const urgentCount   = allRows.filter(r => r.level === 'Urgent').length;

        const incomingRows     = allRows.filter(r => r.type === 'Incoming');
        const outgoingRows     = allRows.filter(r => r.type === 'Outgoing');
        const incomingActive   = incomingRows.filter(r => !r.isCompleted).length;
        const incomingCompleted = incomingRows.filter(r =>  r.isCompleted).length;
        const outgoingActive   = outgoingRows.filter(r => !r.isCompleted).length;
        const outgoingCompleted = outgoingRows.filter(r =>  r.isCompleted).length;

        setStats({
          totalStudents:      students.length,
          incomingCount:      incoming.length,
          outgoingCount:      outgoing.length,
          incomingActive,
          incomingCompleted,
          outgoingActive,
          outgoingCompleted,
          urgentCount,
          partnerCount,
          countryCount,
        });

        /* ── Pipeline: exclude complete students, sort by score then alpha ── */
        const sorted = allRows
          .filter(r => r.score > 0)
          .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.student.localeCompare(b.student);
          });

        setPipeline(sorted);
        retryCountRef.current = 0;

    } catch (err) {
      // Suppress errors caused by an intentional abort (timeout or unmount).
      if (signal.aborted) {
        if (retryCountRef.current < MAX_AUTO_RETRIES) {
          const delay = backoffMs(retryCountRef.current);
          retryCountRef.current++;
          console.log(`[Dashboard] fetch aborted (catch) — retry ${retryCountRef.current}/${MAX_AUTO_RETRIES} in ${delay}ms`);
          retryTimerRef.current = setTimeout(() => load(), delay);
        } else {
          setError('Connection issue — could not load the dashboard. Tap retry.');
        }
        return;
      }
      setError(err?.message ?? 'Failed to load dashboard');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [user?.universityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initial load ─────────────────────────────────────────────
  useEffect(() => {
    if (!user?.universityId) return;
    load();
  }, [user?.universityId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Refetch when the user switches back to this tab ───────────
  // Chrome freezes background tabs; in-flight fetches may be aborted
  // by the timeout above. Reload fresh data as soon as the tab is
  // visible again so the dashboard is never stale or stuck.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && user?.universityId) {
        retryCountRef.current = 0; // New visibility event = fresh retry budget
        setError(null);            // clear any previous "tap retry" banner
        load();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.universityId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Abort on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      clearTimeout(retryTimerRef.current);
    };
  }, []);

  // Manual retry: resets the retry budget and triggers a fresh load.
  // Used by the dashboard's "Retry" button when the auto-retry budget is
  // exhausted and an error is shown.
  const refetch = useCallback(() => {
    retryCountRef.current = 0;
    setError(null);
    setLoading(true);
    load();
  }, [load]);

  return { stats, pipeline, loading, error, refetch };
}
